import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { useState, useRef, useEffect, useCallback } from 'react';
import { MainContents } from '@/components/MainContents';
import { Button } from '@/components/Button';
import clsx from 'clsx';
import usePostJobPostImportRequest from '@/features/agent/provided_job_posts/api/usePostJobPostImportRequest';

const pollingRouteApi = getRouteApi('/agent/provided_job_posts/new/pdf/$id');

export const Route = createFileRoute('/agent/provided_job_posts/new/pdf/')({
  component: ProvidedJobPostsNewPdf,
  staticData: { title: 'PDFから取り込み' },
});

function ProvidedJobPostsNewPdf() {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const dropAreaRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = pollingRouteApi.useNavigate();
  const { mutateAsync: uploadFile } = usePostJobPostImportRequest();

  // ファイルバリデーション
  const isValidFile = useCallback((file: File): boolean => {
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('PDFファイルのみアップロード可能です。');
      return false;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('ファイルサイズは5MBまでです。');
      return false;
    }
    return true;
  }, []);

  // アップロード処理
  const handleUpload = useCallback(
    async (file: File) => {
      if (!isValidFile(file)) return;

      setIsUploading(true);
      try {
        const response = await uploadFile({ file });
        navigate({
          to: '/agent/provided_job_posts/new/pdf/$id',
          params: { id: response.encrypt_id },
        });
      } catch (error) {
        alert(error instanceof Error ? error.message : 'アップロードに失敗しました。');
        setIsUploading(false);
      }
    },
    [isValidFile, uploadFile, navigate]
  );

  // ドラッグ&ドロップイベントハンドラー
  useEffect(() => {
    const dropArea = dropAreaRef.current;
    if (!dropArea) return;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleUpload(files[0]);
      }
    };

    dropArea.addEventListener('dragenter', handleDragEnter);
    dropArea.addEventListener('dragleave', handleDragLeave);
    dropArea.addEventListener('dragover', handleDragOver);
    dropArea.addEventListener('drop', handleDrop);

    return () => {
      dropArea.removeEventListener('dragenter', handleDragEnter);
      dropArea.removeEventListener('dragleave', handleDragLeave);
      dropArea.removeEventListener('dragover', handleDragOver);
      dropArea.removeEventListener('drop', handleDrop);
    };
  }, [handleUpload]);

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleUpload(files[0]);
      }
      e.target.value = '';
    },
    [handleUpload]
  );

  return (
    <MainContents>
      <div className="overflow-hidden rounded-sm bg-white p-6 shadow-[0px_0px_2px_0px_rgba(0,0,0,0.2)]">
        <div className="pb-4">
          <h1 className="text-xl leading-[1.4] font-bold text-main">PDFアップロード</h1>
        </div>

        <div className="flex flex-col gap-2">
          <div
            ref={dropAreaRef}
            className={clsx(
              'flex h-[224px] w-full max-w-[812px] flex-col items-center justify-center gap-3 rounded border-2 border-dashed p-6 transition-colors',
              isUploading
                ? 'pointer-events-none border-border bg-gray opacity-50'
                : isDragging
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-gray'
            )}
          >
            <img src="/agent/candidates/icon-upload.svg" alt="" width={48} height={48} />

            <div className="flex flex-col items-center gap-1">
              <p className="text-lg leading-[1.7] text-main">
                {isUploading ? 'アップロード中...' : 'ここにドラッグアンドドロップ'}
              </p>
              {!isUploading && <p className="text-base leading-[1.7] text-main">または</p>}
            </div>

            {!isUploading && (
              <div onClick={(e) => e.stopPropagation()}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  className="hidden"
                  onChange={handleFileInputChange}
                />
                <Button color="secondary" size="md" type="button" onClick={handleButtonClick}>
                  <img
                    src="/agent/candidates/icon-folder.svg"
                    alt=""
                    width={20}
                    height={20}
                    className="mr-1"
                  />
                  ファイルを選択
                </Button>
              </div>
            )}
          </div>

          <p className="text-xs leading-[1.7] text-sub">
            ※アップロードできるファイル数は1度に1ファイル、ファイルサイズは5MBまでです。
          </p>
        </div>
      </div>
    </MainContents>
  );
}
